import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from './useAuth';
import { 
  selectWeightState,
  selectWeightEntries,
  selectWeightEntryIds,
  selectWeightEntriesArray,
  selectWeightGoal,
  selectWeightStats,
  selectWeightLoading,
  selectWeightError,
  fetchWeightEntries,
  fetchWeightGoal,
  addWeightEntry,
  updateWeightEntry,
  deleteWeightEntry,
  updateWeightGoal
} from '../store/slices/weightSlice';
import { WeightEntry, WeightGoal } from '../types/weight';
import { AppDispatch } from '../store';

/**
 * Hook for managing user weight data
 */
export const useWeight = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const userId = user?.uid || '';
  
  const weightState = useSelector(selectWeightState);
  const weightEntries = useSelector(selectWeightEntries);
  const weightEntryIds = useSelector(selectWeightEntryIds);
  const weightEntriesArray = useSelector(selectWeightEntriesArray);
  const weightGoal = useSelector(selectWeightGoal);
  const weightStats = useSelector(selectWeightStats);
  const isLoading = useSelector(selectWeightLoading);
  const error = useSelector(selectWeightError);
  
  // State flags to prevent multiple fetches
  const [hasInitializedEntries, setHasInitializedEntries] = useState(false);
  const [hasInitializedGoal, setHasInitializedGoal] = useState(false);
  
  // Load weight entries
  useEffect(() => {
    if (userId && !hasInitializedEntries) {
      dispatch(fetchWeightEntries(userId));
      setHasInitializedEntries(true);
    }
  }, [dispatch, userId, hasInitializedEntries]);
  
  // Load weight goal
  useEffect(() => {
    if (userId && !hasInitializedGoal) {
      dispatch(fetchWeightGoal(userId));
      setHasInitializedGoal(true);
    }
  }, [dispatch, userId, hasInitializedGoal]);
  
  // Add weight entry
  const addEntry = useCallback(
    (weight: number, date: number, unit: 'kg' | 'lb', notes?: string) => {
      if (!userId) return null;
      
      return dispatch(
        addWeightEntry({
          userId,
          weight,
          date,
          unit,
          notes
        })
      );
    },
    [dispatch, userId]
  );
  
  // Update weight entry
  const updateEntry = useCallback(
    (entryId: string, data: Partial<Omit<WeightEntry, 'id' | 'userId' | 'createdAt'>>) => {
      if (!userId) return null;
      
      return dispatch(
        updateWeightEntry({
          entryId,
          data: {
            ...data
          } as any // Use type assertion to avoid TypeScript error
        })
      );
    },
    [dispatch, userId]
  );
  
  // Delete weight entry
  const deleteEntry = useCallback(
    (entryId: string) => {
      if (!userId) return null;
      
      return dispatch(deleteWeightEntry(entryId));
    },
    [dispatch, userId]
  );
  
  // Update weight goal
  const updateGoal = useCallback(
    (goal: Partial<WeightGoal>) => {
      if (!userId) return null;
      
      return dispatch(
        updateWeightGoal({
          userId,
          goal
        })
      );
    },
    [dispatch, userId]
  );
  
  // Create default weight goal
  const createDefaultGoal = useCallback(
    (targetWeight: number, deadline: number, unit: 'kg' | 'lb') => {
      if (!userId) return null;
      
      let initialWeight = 0;
      let currentWeight = 0;
      
      // If there are entries, get the initial and current weight
      if (weightEntriesArray.length > 0) {
        initialWeight = weightEntriesArray[weightEntriesArray.length - 1].weight || weightEntriesArray[weightEntriesArray.length - 1].weightValue || 0; // Oldest entry
        currentWeight = weightEntriesArray[0].weight || weightEntriesArray[0].weightValue || 0; // Newest entry
      }
      
      const goal: WeightGoal = {
        isEnabled: true,
        initialWeight,
        currentWeight,
        targetWeight,
        weightTarget: targetWeight, // Original app uses weightTarget
        deadline,
        unit,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      return dispatch(
        updateWeightGoal({
          userId,
          goal
        })
      );
    },
    [dispatch, userId, weightEntriesArray]
  );
  
  // Toggle weight goal enabled state
  const toggleGoalEnabled = useCallback(
    (enabled: boolean) => {
      if (!userId || !weightGoal) return null;
      
      return dispatch(
        updateWeightGoal({
          userId,
          goal: {
            ...weightGoal,
            isEnabled: enabled
          }
        })
      );
    },
    [dispatch, userId, weightGoal]
  );
  
  // Convert between kg and lb
  const kgToLb = useCallback((kg: number) => kg * 2.20462262, []);
  const lbToKg = useCallback((lb: number) => lb * 0.45359237, []);
  
  // Format weight for display
  const formatWeight = useCallback(
    (weight: number, unit: 'kg' | 'lb', precision = 1) => {
      return `${weight.toFixed(precision)} ${unit}`;
    },
    []
  );
  
  return {
    weightState,
    weightEntries,
    weightEntryIds,
    weightEntriesArray,
    weightGoal,
    weightStats,
    isLoading,
    error,
    addEntry,
    updateEntry,
    deleteEntry,
    updateGoal,
    createDefaultGoal,
    toggleGoalEnabled,
    kgToLb,
    lbToKg,
    formatWeight
  };
};